import to from 'await-to-js';
import axios from 'axios';
import { find as _find, range, get, isEmpty, keyBy, reduce, size, sortBy, uniqBy } from 'lodash';
import { ref, computed } from 'vue';
import ClusterSchemeTypes from '@enums/cluster-schemes-source';
import i18n from '@/plugins/vue-i18n';
import { useAlertsStore } from '@/stores/alerts';
import { useSnackbarStore } from '@/stores/snackbar';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useWorkpackagesStore } from '@/stores/workpackages';
import { defineStore } from 'pinia';
import { useEditableTableStore } from '@/stores/utils/editable-table-store-utils';
import handleErrorMessages from '@/stores/utils/validation';
import { jobApi } from '@/helpers';
import { useScenariosStore } from './scenarios';

export const useClusteringStore = defineStore('clustering', () => {
  const scenariosStore = useScenariosStore();
  const workpackagesStore = useWorkpackagesStore();
  const snackbarStore = useSnackbarStore();
  const alertsStore = useAlertsStore();

  const {
    // States
    localDataGetterName,
    localDataChangesPerCell,
    localTableRowContents,
    localInvalidErrors,
    // Getters
    getTableRows,
    hasInvalidValuesInAnyCell,
    userMadeChangesToAnyCell,
    hasDataIn,
    hasChangesInCell,
    hasInvalidDataInCell,
    // Actions
    setNameOfLocalGetter,
    setTableRows,
    setInputValue,
    setInputError,
    setCellChanges,
    unloadData,
    markAllRowsAsChanged,
    initialiseEditableTable,
    onValueChanged,
    setUploadedData,
  } = useEditableTableStore();
  // States
  const loading = ref(false);
  const clusterSchemes = ref([]);
  const workpackageScenarioClusterSchemes = ref([]);
  const clusterSchemeInformation = ref({});
  const isLoadingClusterInformation = ref(false);
  const isLoadingClusterSchemes = ref(false);
  const selectedScheme = ref(null);
  const selectedAttributeId = ref(null);
  const comparisonSchemeId = ref(null);
  const selectableClusterNames = ref([]);
  const availableKMeans = range(1, 11); // Allow creation of 1-10 schemes

  // Getters
  const noSelectedClusterScheme = computed(() => {
    const selectedClusteringScheme = _find(clusterSchemes.value, { selected: true });
    return isEmpty(selectedClusteringScheme);
  });

  const getScenarioAttributes = computed(() => {
    const attributes = scenariosStore.getCustomerFacingAndReportingAttributes;

    return (attributes || []).map(attribute => ({ ...attribute, selected: true }));
  });

  const getScenarioClusterSchemes = computed(() => {
    return sortBy(clusterSchemes.value, ['clusterCount', '_id']);
  });
  const getScenarioClusterSchemeById = computed(
    () => id => clusterSchemes.value.find(el => el._id === id)
  );

  const getScenarioClusterSchemesForExport = computed(() => {
    // returns flat array of stores mapped to clusters and schemes, same format as would be uploaded
    // get workpackage stores to look up display keys
    const scenarioStoreMap = keyBy(scenariosStore.stores, 'storeKey');

    // get cluster schemes sorted same as FE
    const sortedClusterSchemes = getScenarioClusterSchemes.value;
    const sources = [
      ClusterSchemeTypes.kmeans,
      ClusterSchemeTypes.kmeansEdited,
      ClusterSchemeTypes.upload,
      ClusterSchemeTypes.uploadEdited,
    ];

    const storeToClusterAndSchemeMappings = [];
    const badStoreKeys = [];
    sortedClusterSchemes.forEach(scheme => {
      scheme.clusters.forEach(cluster => {
        cluster.storeKeys.forEach(storeKey => {
          const { clusterName } = cluster;
          const { name } = scheme;
          // default clusters from the reference clustering schemes could have a larger subset of stores
          if (scenarioStoreMap[storeKey] === undefined) {
            if (sources.includes(scheme.source)) {
              badStoreKeys.push(storeKey);
            }
          } else {
            const { storeKeyDisplay } = scenarioStoreMap[storeKey];
            storeToClusterAndSchemeMappings.push({ storeKeyDisplay, clusterName, name });
          }
        });
      });
    });
    if (badStoreKeys.length > 0) {
      console.log(`${i18n.t('errors.storesNotInScope')}, ${badStoreKeys.join()}`); // TODO: need to dispatch warning
    }
    return storeToClusterAndSchemeMappings;
  });

  const getClusterSchemeAttributes = computed(() => {
    const allAttributes = scenariosStore.getCustomerFacingAndReportingAttributes;

    const clusterSchemeAttributes = selectedScheme.value.selectedAttributeIds.map(id =>
      _find(allAttributes, { id })
    );
    return sortBy(clusterSchemeAttributes, item => item.name.toLowerCase());
  });

  const getAttributeDeviations = computed(() => {
    const attribute = _find(clusterSchemeInformation.value.deviations, {
      attributeId: selectedAttributeId.value,
    });

    return attribute ? attribute.attributeStoreDeviations : [];
  });

  const selectedClusterScheme = computed(() => {
    const selected = _find(clusterSchemes.value, scheme => {
      return scheme.selected === true && scheme.clusterCount > 1;
    });
    // if we don't have any selected scheme (i.e. clustered switching is not run), just pick the first scheme or return empty object
    return selected || get(clusterSchemes.value, 0, {});
  });

  const wpScenarioClustersMap = computed(() => {
    return reduce(
      workpackageScenarioClusterSchemes.value,
      (acc, scheme) => {
        return {
          ...acc,
          [scheme.scenarioId]: scheme.clusters,
        };
      },
      {}
    );
  });

  // Actions
  function refreshSelectedScheme() {
    const internalSelectedScheme = _find(clusterSchemes.value, { selected: true }) || null;

    selectedScheme.value = internalSelectedScheme;
  }
  async function fetchScenarioClusters() {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(axios.get(`/api/scenarios/${scenarioId}/cluster-schemes`));
    loading.value = false;
    if (err) throw new Error(err.message);
    clusterSchemes.value = response.data;
    refreshSelectedScheme();
    return response.data;
  }
  async function updateClusters(updates) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const selectedClusterSchemeId = selectedScheme.value._id;
    const clusterStoreKeys = updates.map(c => c.storeKeys);
    const jobApiRes = await jobApi.runFunction('recalculate-cluster-variance-explained', {
      scenario_id: scenarioId,
      scheme_id: selectedClusterSchemeId,
      clusters: clusterStoreKeys,
    });
    const { variance } = jobApiRes;
    const clusterCount = uniqBy(updates, 'clusterName').length;
    loading.value = true;
    const [err] = await to(
      axios.patch(
        `/api/scenarios/${scenarioId}/cluster-schemes/${selectedClusterSchemeId}/clusters`,
        { updates, variance, clusterCount }
      )
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    fetchScenarioClusters();
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }

  async function fetchScenarioClustersByWorkpackage(options = {}) {
    const workpackageId = options.workpackageId || workpackagesStore.selectedWorkpackage._id;
    const pick = ['scenarioId', 'name', 'clusters'];
    loading.value = true;
    const [err, response] = await to(
      axios.get(`/api/workpackages/${workpackageId}/clusters`, { params: { pick } })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    if (!options.shouldCommit) return response.data;
    workpackageScenarioClusterSchemes.value = response.data;
  }

  async function fetchClusterSchemesFromFeed() {
    const scenarioId = scenariosStore.selectedScenario._id;
    const workpackageId = workpackagesStore.selectedWorkpackage._id;
    const [err, response] = await to(
      axios.get(`/api/scenarios/${scenarioId}/cluster-schemes/feed`, { params: { workpackageId } })
    );
    if (err) {
      const messageKey = get(err, 'response.data.messageKey', err.message);
      const messageData = get(err, 'response.data.messageParams', []);
      const errorMessage = [{ messageKey, data: messageData }];

      snackbarStore.showError(i18n.t('errors.clustersFromFeed'));
      if (size(messageData)) alertsStore.showMessages(errorMessage);

      return {};
    }
    return response.data;
  }

  async function removeFailedClusters({ scenarioId }) {
    loading.value = true;
    const url = `/api/scenarios/${scenarioId}/failed-cluster-schemes`;
    const [err] = await to(axios.delete(url));
    loading.value = false;
    if (err) throw new Error(err.message);
  }

  async function saveClusteringSchemes(bodyData) {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    isLoadingClusterSchemes.value = true;
    const [err, response] = await to(
      axios.post(`/api/scenarios/${scenarioId}/cluster-schemes`, bodyData)
    );
    const options = { displayEmpty: false };
    handleErrorMessages({ response, options });
    // if the job failed to start due to a validation error, we need to remove failed clusters.
    // same as if clusteringJobFailed.
    if (get(response, 'data.messageType') === 'JobValidation') {
      await removeFailedClusters({ scenarioId });
    }

    loading.value = false;

    if (err) {
      throw new Error(get(err, 'message') || err);
    }

    fetchScenarioClusters();
  }

  async function getClusterSchemeData(clusterScheme) {
    const { _id, selectedAttributeIds } = clusterScheme;
    const scenarioId = scenariosStore.selectedScenario._id;
    const url = `/api/scenarios/${scenarioId}/cluster-scheme/${_id}`;
    const params = { attributeIds: selectedAttributeIds };

    isLoadingClusterInformation.value = true;
    const [err, response] = await to(axios.get(url, { params }));
    isLoadingClusterInformation.value = false;
    if (err) throw new Error(err.message);
    clusterSchemeInformation.value = response.data;
  }
  async function finishClusteringJob({ message, translation, color }) {
    const [err, res] = await to(axios.get(`/api/scenarios/${message.scenarioId}`));
    if (err) throw new Error(err.message);
    const scenario = res.data.data;
    const scenarioIdFromEvent = get(message, 'scenarioId', null);
    const scenarioId = scenariosStore.selectedScenario._id;
    // do not refresh store in case user navigated away from current scenario
    if (scenarioIdFromEvent === scenarioId) {
      fetchScenarioClusters();
    }

    const content = i18n.t(translation, { name: scenario.name });
    snackbarStore.showSnackbar({ content, color });
    isLoadingClusterSchemes.value = false;
  }

  function clusteringJobComplete({ message }) {
    finishClusteringJob({
      message,
      translation: 'notifications.clustering.finished',
      color: 'success',
    });
  }

  async function clusteringJobFailed({ message }) {
    // wait for removeFailedClusters before fetching clusters
    await removeFailedClusters(message);
    finishClusteringJob({
      message,
      translation: 'notifications.clustering.failed',
      color: 'error',
    });
  }

  function clusteredSwitchingJobComplete({ message }) {
    finishClusteringJob({
      message,
      translation: 'notifications.clusteredSwitching.finished',
      color: 'success',
    });
  }
  function clusteredSwitchingJobFailed({ message }) {
    finishClusteringJob({
      message,
      translation: 'notifications.clusteredSwitching.failed',
      color: 'error',
    });
  }

  function changeSelectedAttribute(selectedAttributeIdRaw) {
    const attributeWasSelectedInScheme = _find(getClusterSchemeAttributes.value, {
      id: selectedAttributeIdRaw,
    });

    selectedAttributeId.value = attributeWasSelectedInScheme ? selectedAttributeIdRaw : null;
  }

  async function runClusteredSwitching(payload) {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const url = `/api/scenarios/${scenarioId}/cluster-schemes/switching`;
    const [err] = await to(axios.post(url, payload));
    loading.value = false;
    if (err) throw new Error(err.message);
  }

  async function processCSV({ fileId, mappings, delimiter }) {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const body = { mappings, scenarioId, delimiter };
    const [err, { data: responseData } = {}] = await to(
      axios.post(`/api/csv/clustering/process/${fileId}`, body)
    );
    loading.value = false;
    // TODO: Remove this and use global interceptor on AOV3-159
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);
      return Promise.reject(message);
    }

    if (size(responseData.messages)) {
      alertsStore.showMessages(responseData.messages);
      return {}; // AOV3-196 NOTE: should not upload anything if there are any errors.
    }

    return responseData.storeToClusterMappings;
  }

  function setSelectedScheme(newSelectedScheme) {
    selectedScheme.value = newSelectedScheme;
  }

  function updateSelectedAttributeId() {
    const selectedAttributes = selectedScheme.value
      ? selectedScheme.value.selectedAttributeIds
      : [];
    // if the selected scheme does not uses the currently selected attr, do not show it
    if (!selectedAttributes.includes(selectedAttributeId.value)) {
      selectedAttributeId.value = null;
    }
  }

  function setComparisonSchemeId(newComparisonSchemeId) {
    comparisonSchemeId.value = newComparisonSchemeId;
  }

  function setSelectableClusterNames(clusterNames) {
    selectableClusterNames.value = [...clusterNames, i18n.t('clusteringPage.newCluster')];
  }

  function setSelectedSchemeClusters(clusters) {
    selectedScheme.value.clusters = clusters;
  }

  return {
    loading,
    clusterSchemes,
    workpackageScenarioClusterSchemes,
    clusterSchemeInformation,
    isLoadingClusterInformation,
    isLoadingClusterSchemes,
    selectedScheme,
    selectedAttributeId,
    comparisonSchemeId,
    selectableClusterNames,
    // Getters
    noSelectedClusterScheme,
    getScenarioAttributes,
    getScenarioClusterSchemes,
    getScenarioClusterSchemeById,
    getScenarioClusterSchemesForExport,
    getClusterSchemeAttributes,
    getAttributeDeviations,
    selectedClusterScheme,
    wpScenarioClustersMap,
    // Actions
    refreshSelectedScheme,
    fetchScenarioClusters,
    updateClusters,
    fetchScenarioClustersByWorkpackage,
    fetchClusterSchemesFromFeed,
    removeFailedClusters,
    saveClusteringSchemes,
    getClusterSchemeData,
    finishClusteringJob,
    clusteringJobFailed,
    clusteringJobComplete,
    clusteredSwitchingJobComplete,
    clusteredSwitchingJobFailed,
    changeSelectedAttribute,
    runClusteredSwitching,
    processCSV,
    setSelectedScheme,
    updateSelectedAttributeId,
    setComparisonSchemeId,
    setSelectableClusterNames,
    setSelectedSchemeClusters,
    // Editable table
    // States
    localDataGetterName,
    localDataChangesPerCell,
    localTableRowContents,
    localInvalidErrors,
    availableKMeans,
    // Getters
    getTableRows,
    hasInvalidValuesInAnyCell,
    userMadeChangesToAnyCell,
    hasDataIn,
    hasChangesInCell,
    hasInvalidDataInCell,
    // Actions
    setNameOfLocalGetter,
    setTableRows,
    setInputValue,
    setInputError,
    setCellChanges,
    unloadData,
    markAllRowsAsChanged,
    initialiseEditableTable,
    onValueChanged,
    setUploadedData,
  };
});
